import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { icon, type IconName } from './icons';

/**
 * <cadence-stat-card heading="…" icon="users"> — a flat KPI/stat tile.
 *
 * Owns the shared box chrome (background, border, radius, padding) and the small
 * uppercase icon+label header used by the dashboard and accounting overview tiles.
 * The tile body goes in the default slot (value, sparkline, breakdown rows, feed,
 * …); those content styles stay with the consuming page, since slotted content is
 * styled in the page's own shadow scope. `icon` is optional — omit for a plain
 * label-only header. Centralising the chrome here keeps the tiles visually
 * consistent and gives theming a single place to restyle.
 */
@customElement('cadence-stat-card')
export class CadenceStatCard extends LitElement {
  @property() heading = '';
  /** Optional lucide icon name (kebab-case) rendered before the heading. */
  @property() icon = '';

  static styles = css`
    :host { display: block; height: 100%; }
    .stat {
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-lg); padding: 18px; height: 100%; box-sizing: border-box;
      display: flex; flex-direction: column; gap: 6px;
    }
    .top {
      display: flex; align-items: center; gap: 8px; color: var(--color-text-secondary);
      font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em;
    }
    .top .label { display: inline-flex; align-items: center; gap: 8px; }
    /* Optional header action (e.g. a "view all" link) pinned to the right; reset the
       uppercase header styling so the slotted content renders as normal text. */
    .top .actions { margin-left: auto; text-transform: none; letter-spacing: normal; }
    .top svg { width: 16px; height: 16px; }
  `;

  render() {
    return html`
      <div class="stat">
        ${this.heading
          ? html`<span class="top">
              <span class="label">${this.icon ? icon(this.icon as IconName) : ''} ${this.heading}</span>
              <span class="actions"><slot name="actions"></slot></span>
            </span>`
          : ''}
        <slot></slot>
      </div>`;
  }
}
